/**
 * accountFormatters.ts — 2026-06-06 source-of-truth-full-ui.
 *
 * Single source of formatting rules for all account values surfaced in
 * the Trading UI. Reuse everywhere: Dashboard / Portfolio / Account
 * Summary / Connected Brokerage / Portfolio Settings / Risk Center /
 * Header / Positions / Open Orders.
 *
 * Contract:
 *   formatUsd(236724.5)   -> "$236,724.50"
 *   formatPnL(472.81)     -> "+$472.81"
 *   formatPnL(-472.81)    -> "-$472.81"
 *   formatPnL(null)       -> "—"
 *   formatPercent(1.25)   -> "+1.25%"
 *   formatPercent(-0.84)  -> "-0.84%"
 *
 * Inputs accept number | string | null | undefined; non-finite values
 * render the em-dash placeholder (`—`). NEVER renders `$0` for missing.
 */

export const EM_DASH = '—';

function _parse(v: number | string | null | undefined): number | null {
  if (v === null || v === undefined || v === '') return null;
  if (typeof v === 'number') return Number.isFinite(v) ? v : null;
  const cleaned = String(v).replace(/[\s,$]/g, '');
  const n = Number(cleaned);
  return Number.isFinite(n) ? n : null;
}

/** Currency, two decimals, comma grouping. `$236,724.50` */
export function formatUsd(v: number | string | null | undefined): string {
  const n = _parse(v);
  if (n === null) return EM_DASH;
  const sign = n < 0 ? '-' : '';
  return `${sign}$${Math.abs(n).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

/** Signed currency (P&L). `+$472.81` / `-$472.81` / `—` */
export function formatPnL(v: number | string | null | undefined): string {
  const n = _parse(v);
  if (n === null) return EM_DASH;
  if (n === 0) return '$0.00';
  const sign = n > 0 ? '+' : '-';
  return `${sign}$${Math.abs(n).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

/** Signed percent, two decimals. `+1.25%` / `-0.84%` / `—` */
export function formatPercent(v: number | string | null | undefined): string {
  const n = _parse(v);
  if (n === null) return EM_DASH;
  if (n === 0) return '0.00%';
  const sign = n > 0 ? '+' : '-';
  return `${sign}${Math.abs(n).toFixed(2)}%`;
}

/** Plain integer count with commas. `1,234,567` / `—` */
export function formatCount(v: number | string | null | undefined): string {
  const n = _parse(v);
  if (n === null) return EM_DASH;
  return Math.round(n).toLocaleString();
}

/** "Just now" / "5 min ago" / locale time. */
export function formatLastSyncedAt(iso: string | null | undefined): string {
  if (!iso) return EM_DASH;
  const stamp = String(iso).endsWith('Z') ? iso : iso + 'Z';
  const ts = Date.parse(stamp);
  if (!Number.isFinite(ts)) return EM_DASH;
  const ageMs = Date.now() - ts;
  if (ageMs < 0) return new Date(ts).toLocaleTimeString();
  if (ageMs < 30_000) return 'Just now';
  if (ageMs < 60 * 60_000) return `${Math.round(ageMs / 60_000)} min ago`;
  return new Date(ts).toLocaleTimeString();
}

/**
 * 2026-06-07 settings-emergency-quotes-refresh-final.
 *
 * Safe local-time formatter for any backend timestamp. Returns
 * `fallback` when the input is null/undefined/empty AND when the
 * input parses to an invalid Date (would otherwise render the
 * literal browser string "Invalid Date").
 */
export function safeFormatTimestamp(
  iso: string | null | undefined,
  opts?: { fallback?: string; invalidFallback?: string; mode?: "datetime" | "time" | "date" },
): string {
  const fallback = opts?.fallback ?? EM_DASH;
  const invalidFallback = opts?.invalidFallback ?? "Sync timestamp unavailable";
  const mode = opts?.mode ?? "datetime";
  if (iso === null || iso === undefined || iso === "") return fallback;
  const raw = String(iso);
  const stamp = raw.endsWith("Z") || /[+-]\d{2}:?\d{2}$/.test(raw) ? raw : raw + "Z";
  const ts = Date.parse(stamp);
  if (!Number.isFinite(ts)) return invalidFallback;
  const d = new Date(ts);
  if (Number.isNaN(d.getTime())) return invalidFallback;
  if (mode === "time") return d.toLocaleTimeString();
  if (mode === "date") return d.toLocaleDateString();
  return d.toLocaleString();
}

/**
 * Sync status label per the canonical spec.
 *
 *   - sync_status === "sync_failed"  -> "Sync failed"
 *   - sync_status === "sync_pending" -> "Sync pending"
 *   - timestamp present + parseable    -> formatted local date/time
 *   - timestamp null/missing           -> "Never synced"
 *   - timestamp present but bad        -> "Sync timestamp unavailable"
 */
export function formatSyncStateLabel(
  args: { timestamp: string | null | undefined; sync_status?: string | null },
): string {
  const status = args.sync_status ?? null;
  if (status === "sync_failed") return "Sync failed";
  if (status === "sync_pending") return "Sync pending";
  if (args.timestamp === null || args.timestamp === undefined || args.timestamp === "") {
    return "Never synced";
  }
  return safeFormatTimestamp(args.timestamp, {
    fallback: "Never synced",
    invalidFallback: "Sync timestamp unavailable",
    mode: "datetime",
  });
}

/** Translate backend action verb -> friendly label. */
export type AccountCall =
  | 'BUY'
  | 'SELL'
  | 'SHORT'
  | 'COVER'
  | 'HOLD'
  | 'AVOID'
  | null
  | undefined;

export function translateCall(call: AccountCall | string): string {
  switch ((call || '').toUpperCase()) {
    case 'BUY':   return 'Long Bias';
    case 'SELL':  return 'Exit / Sell Bias';
    case 'SHORT': return 'Short Bias';
    case 'COVER': return 'Cover Short';
    case 'HOLD':  return 'Hold';
    case 'AVOID': return 'Avoid';
    default:      return '';
  }
}

/** Connection state label. */
export type SyncStatus = 'not_connected' | 'synced' | 'sync_pending' | 'sync_failed' | string | null | undefined;

export function brokerConnectionLabel(
  brokerConnected: boolean,
  syncStatus: SyncStatus,
): string {
  if (!brokerConnected) return 'No brokerage account connected';
  switch (syncStatus) {
    case 'synced':       return 'Alpaca Paper connected — Synced';
    case 'sync_pending': return 'Alpaca Paper connected — Sync pending';
    case 'sync_failed':  return 'Alpaca Paper connected — Sync unavailable';
    default:             return 'Alpaca Paper connected';
  }
}

export function syncStatusLabel(syncStatus: SyncStatus): string {
  switch (syncStatus) {
    case 'synced':        return 'Synced';
    case 'sync_pending':  return 'Pending';
    case 'sync_failed':   return 'Failed';
    case 'not_connected': return 'Not connected';
    default:              return EM_DASH;
  }
}


// 2026-06-06 g5-date-formatter — global date / datetime / relative-time
// formatters paired with the currency / percent / P&L helpers above so
// every Trading UI surface uses one set of rules.

/** "2026-06-06 17:08 UTC" — unambiguous, no locale surprises. */
export function formatDateTime(iso: string | null | undefined): string {
  if (!iso) return EM_DASH;
  const stamp = String(iso).endsWith('Z') ? iso : iso.replace(' ', 'T') + 'Z';
  const ts = Date.parse(stamp);
  if (!Number.isFinite(ts)) return EM_DASH;
  const d = new Date(ts);
  const pad = (n: number) => String(n).padStart(2, '0');
  return (
    d.getUTCFullYear() + '-' +
    pad(d.getUTCMonth() + 1) + '-' +
    pad(d.getUTCDate()) + ' ' +
    pad(d.getUTCHours()) + ':' +
    pad(d.getUTCMinutes()) + ' UTC'
  );
}

/** "2026-06-06" date-only. */
export function formatDate(iso: string | null | undefined): string {
  if (!iso) return EM_DASH;
  const stamp = String(iso).endsWith('Z') ? iso : iso.replace(' ', 'T') + 'Z';
  const ts = Date.parse(stamp);
  if (!Number.isFinite(ts)) return EM_DASH;
  const d = new Date(ts);
  const pad = (n: number) => String(n).padStart(2, '0');
  return d.getUTCFullYear() + '-' + pad(d.getUTCMonth() + 1) + '-' + pad(d.getUTCDate());
}

/** "Just now" / "5 min ago" / "2 h ago" / falls back to formatDateTime. */
export function formatRelativeTime(iso: string | null | undefined): string {
  if (!iso) return EM_DASH;
  const stamp = String(iso).endsWith('Z') ? iso : iso.replace(' ', 'T') + 'Z';
  const ts = Date.parse(stamp);
  if (!Number.isFinite(ts)) return EM_DASH;
  const ageMs = Date.now() - ts;
  if (ageMs < 0) return formatDateTime(iso);
  if (ageMs < 30_000) return 'Just now';
  if (ageMs < 60 * 60_000) return `${Math.round(ageMs / 60_000)} min ago`;
  if (ageMs < 24 * 60 * 60_000) return `${Math.round(ageMs / (60 * 60_000))} h ago`;
  return formatDate(iso);
}
